/**
 * Swatch Show More functionality
 * Shows only 7 swatches initially, with a "+X" button to show remaining
 */
// Prevent duplicate declaration if script is loaded multiple times
if (typeof window.SwatchShowMore === 'undefined') {
  window.SwatchShowMore = class SwatchShowMore {
  constructor() {
    this.processedContainers = new Set();
    this.expandedContainers = new Set(); // Track which containers have been expanded
    // Store expanded state globally to persist across DOM updates
    if (!window.swatchShowMoreState) {
      window.swatchShowMoreState = {
        expandedOptions: new Set() // Store by option index
      };
    }
    this.init();
  }

  init() {
    // Get all containers
    const allContainers = document.querySelectorAll('.swatch-options-container');
    
    allContainers.forEach((container) => {
      // Get option index to track expanded state persistently
      const optionIndex = container.getAttribute('data-option-index');
      const isExpandedInState = optionIndex && window.swatchShowMoreState.expandedOptions.has(optionIndex);
      const isExpandedInDOM = container.hasAttribute('data-expanded');
      const isExpanded = isExpandedInState || isExpandedInDOM;
      
      if (isExpanded) {
        // Mark as expanded in both DOM and state
        if (optionIndex) {
          window.swatchShowMoreState.expandedOptions.add(optionIndex);
        }
        container.setAttribute('data-expanded', 'true');
        
        // Preserve expanded state - ensure all swatches are visible
        this.expandedContainers.add(container);
        const swatches = container.querySelectorAll('.swatch-item');
        swatches.forEach((swatch) => {
          swatch.classList.remove('swatch-item--hidden', 'swatch-item--no-transition');
        });
        // Remove any existing show more button
        const existingButton = container.querySelector('.swatch-show-more-btn');
        if (existingButton) {
          existingButton.remove();
        }
        return; // Skip processing for expanded containers
      }

      // Only process if not already processed or if button doesn't exist
      const hasButton = container.querySelector('.swatch-show-more-btn');
      const isProcessed = container.hasAttribute('data-show-more-processed');
      
      if (!isProcessed || !hasButton) {
        // Mark as processed
        container.setAttribute('data-show-more-processed', 'true');
        this.processedContainers.add(container);
        
        const swatches = container.querySelectorAll('.swatch-item');
        const totalSwatches = swatches.length;
        const maxVisible = 7;

        // Only apply if there are more than 7 swatches and no button exists
        if (totalSwatches > maxVisible && !hasButton) {
          // Add hidden class immediately without transition to prevent fade-in on load
          swatches.forEach((swatch, index) => {
            if (index >= maxVisible) {
              swatch.classList.add('swatch-item--hidden', 'swatch-item--no-transition');
              // Remove no-transition class after a brief delay to allow transitions for future changes
              setTimeout(() => {
                swatch.classList.remove('swatch-item--no-transition');
              }, 50);
            }
          });

          // Create and insert the "+X" button
          const remainingCount = totalSwatches - maxVisible;
          const showMoreButton = this.createShowMoreButton(remainingCount);
          
          // Insert button after the last visible swatch
          const lastVisibleSwatch = swatches[maxVisible - 1];
          if (lastVisibleSwatch) {
            lastVisibleSwatch.after(showMoreButton);
          } else {
            container.appendChild(showMoreButton);
          }

          // Add click handler
          showMoreButton.addEventListener('click', () => {
            this.showAllSwatches(container, showMoreButton);
          });
        }
      }
    });
  }

  createShowMoreButton(remainingCount) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'swatch-show-more-btn';
    button.setAttribute('aria-label', `Show ${remainingCount} more colors`);
    button.innerHTML = `<span class="swatch-show-more-text">+${remainingCount}</span>`;
    return button;
  }

  showAllSwatches(container, button) {
    const hiddenSwatches = Array.from(container.querySelectorAll('.swatch-item--hidden'));
    
    if (hiddenSwatches.length === 0) {
      button.remove();
      return;
    }

    // Mark container as expanded in both DOM and persistent state
    const optionIndex = container.getAttribute('data-option-index');
    if (optionIndex) {
      window.swatchShowMoreState.expandedOptions.add(optionIndex);
    }
    container.setAttribute('data-expanded', 'true');
    this.expandedContainers.add(container);

    // Remove the button with fade out
    button.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    button.style.opacity = '0';
    button.style.transform = 'scale(0.8)';
    setTimeout(() => {
      button.remove();
    }, 200);

    // Show swatches with staggered animation
    hiddenSwatches.forEach((swatch, index) => {
      setTimeout(() => {
        swatch.classList.remove('swatch-item--hidden');
        swatch.classList.add('swatch-item--revealing');
      }, 100 + (index * 60)); // Staggered delay: 100ms initial + 60ms per item
    });
  }
  };
}

// Initialize when DOM is ready
function initializeSwatchShowMore() {
  if (window.SwatchShowMore) {
    new window.SwatchShowMore();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSwatchShowMore);
} else {
  initializeSwatchShowMore();
}

// Re-initialize when variant selects are updated (for dynamic content)
// But preserve expanded state
document.addEventListener('DOMContentLoaded', () => {
  if (!window.SwatchShowMore) return;
  let swatchShowMoreInstance = new window.SwatchShowMore();
  
  // Listen to variant change events to preserve state
  if (window.FoxTheme && window.FoxTheme.pubsub) {
    window.FoxTheme.pubsub.subscribe(
      window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      () => {
        // Preserve expanded state before DOM updates
        const expandedContainers = document.querySelectorAll('.swatch-options-container[data-expanded="true"]');
        expandedContainers.forEach((container) => {
          const optionIndex = container.getAttribute('data-option-index');
          if (optionIndex) {
            window.swatchShowMoreState.expandedOptions.add(optionIndex);
          }
        });
        
        // Function to restore expanded state immediately
        const restoreExpandedState = () => {
          const allContainers = document.querySelectorAll('.swatch-options-container');
          allContainers.forEach((container) => {
            const optionIndex = container.getAttribute('data-option-index');
            if (optionIndex && window.swatchShowMoreState.expandedOptions.has(optionIndex)) {
              container.setAttribute('data-expanded', 'true');
              // Also ensure swatches are visible
              const swatches = container.querySelectorAll('.swatch-item');
              swatches.forEach((swatch) => {
                swatch.classList.remove('swatch-item--hidden', 'swatch-item--no-transition');
              });
            }
          });
        };
        
        // Restore immediately (synchronous)
        restoreExpandedState();
        
        // Also restore after DOM updates (using requestAnimationFrame for newly added elements)
        requestAnimationFrame(() => {
          restoreExpandedState();
        });
        
        // Re-initialize after a short delay to allow DOM to fully update
        setTimeout(() => {
          swatchShowMoreInstance = new window.SwatchShowMore();
        }, 100);
      }
    );
  }
  
  const observer = new MutationObserver((mutations) => {
    // Immediately restore expanded state on newly added containers to prevent blink
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is a container or contains containers
          const containers = node.matches && node.matches('.swatch-options-container')
            ? [node]
            : (node.querySelectorAll ? node.querySelectorAll('.swatch-options-container') : []);
          
          containers.forEach((container) => {
            const optionIndex = container.getAttribute('data-option-index');
            if (optionIndex && window.swatchShowMoreState.expandedOptions.has(optionIndex)) {
              // Restore immediately to prevent CSS from hiding swatches
              container.setAttribute('data-expanded', 'true');
              const swatches = container.querySelectorAll('.swatch-item');
              swatches.forEach((swatch) => {
                swatch.classList.remove('swatch-item--hidden', 'swatch-item--no-transition');
              });
            }
          });
        }
      });
    });
    
    // Debounce to avoid multiple calls
    clearTimeout(window.swatchShowMoreTimeout);
    window.swatchShowMoreTimeout = setTimeout(() => {
      // Create new instance which will preserve expanded state
      swatchShowMoreInstance = new window.SwatchShowMore();
    }, 150);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});

